import { useEffect, useRef } from 'react';
import { colors } from '@alixpartners/ui-components';
import { getMonthByDate } from 'utils/date';

export const usePrevious = value => {
	const ref = useRef();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

export const CHART_COLORS = [
	colors.chartDarkBlue,
	colors.chartBrightTeal,
	colors.chartMediumGrey,
	colors.chartOrange,
	colors.chartDarkGreen,
	colors.chartDarkTeal,
	colors.chartLightBlue,
	colors.chartDarkGrey,
	colors.chartLightOrange,
	'#43AD80',
];

export const SPEND_BY_PROPS = {
	CATEGORY: 'category',
	COMPANY: 'company',
	VENDOR: 'harmonizedSupplier',
	RECEIVING_REGION: 'receivingRegion',
	PURCHASING_REGION: 'purchasingRegion',
	TAIL_SEGMENT: 'segmentName',
};

export const getSpendByOptions = isTailSpend => {
	const options = [...SPEND_BY_OPTIONS];
	if (isTailSpend) {
		options.push({
			label: 'Tail segments',
			value: SPEND_BY_PROPS.TAIL_SEGMENT,
		});
	}
	return options;
};

export const SPEND_BY_OPTIONS = [
	{
		label: 'Categories',
		value: SPEND_BY_PROPS.CATEGORY,
	},
	{
		label: 'Companies',
		value: SPEND_BY_PROPS.COMPANY,
	},
	{
		label: 'Harmonized vendors',
		value: SPEND_BY_PROPS.VENDOR,
	},
	{
		label: 'Receiving regions',
		value: SPEND_BY_PROPS.RECEIVING_REGION,
	},
	{
		label: 'Purchasing regions',
		value: SPEND_BY_PROPS.PURCHASING_REGION,
	},
];

export const SPEND_BY_LABELS = {
	[SPEND_BY_PROPS.CATEGORY]: 'category',
	[SPEND_BY_PROPS.COMPANY]: 'company',
	[SPEND_BY_PROPS.VENDOR]: 'harmonized vendor',
	[SPEND_BY_PROPS.RECEIVING_REGION]: 'receiving region',
	[SPEND_BY_PROPS.PURCHASING_REGION]: 'purchasing region',
	[SPEND_BY_PROPS.TAIL_SEGMENT]: 'tail segment',
};

export const SPEND_BY_LABELS_PLURAL = {
	[SPEND_BY_PROPS.CATEGORY]: 'Categories',
	[SPEND_BY_PROPS.COMPANY]: 'Companies',
	[SPEND_BY_PROPS.VENDOR]: 'Harmonized vendors',
	[SPEND_BY_PROPS.RECEIVING_REGION]: 'Receiving regions',
	[SPEND_BY_PROPS.PURCHASING_REGION]: 'Purchasing regions',
	[SPEND_BY_PROPS.TAIL_SEGMENT]: 'Tail segments',
};

export const BREAKDOWN_BY = {
	NONE: '0',
	TAIL_SEGMENT: '7',
};

export const getBreakdownByOptions = isTailSpend => {
	const options = [...BREAKDOWN_BY_OPTIONS];
	if (isTailSpend) {
		options.push({
			label: 'Tail segments',
			value: BREAKDOWN_BY.TAIL_SEGMENT,
		});
	}
	return options;
};

export const BREAKDOWN_BY_OPTIONS = [
	{
		label: 'None',
		value: BREAKDOWN_BY.NONE,
	},
	{
		label: 'Companies',
		value: '1',
	},
	{
		label: 'Top L1 categories',
		value: '2',
	},
	{
		label: 'Top L4 categories',
		value: '3',
	},
	{
		label: 'Top harmonized vendors',
		value: '4',
	},
	{
		label: 'Top receiving regions',
		value: '5',
	},
	{
		label: 'Top purchasing regions',
		value: '6',
	},
];

export const CHART_TYPES = {
	WATERFALL: 1,
	STACKED: 2,
	STACKED100: 3,
	CLUSTERED: 4,
	BAR: 5,
};

export const CHART_TYPE_MAP = {
	[CHART_TYPES.BAR]: 'column',
	[CHART_TYPES.CLUSTERED]: 'column',
	[CHART_TYPES.STACKED]: 'column',
	[CHART_TYPES.STACKED100]: 'column',
	[CHART_TYPES.WATERFALL]: 'waterfall',
};

export const CHART_TYPE_OPTIONS_WITHOUT_BREAKDOWN = [
	{
		label: 'Waterfall chart',
		value: CHART_TYPES.WATERFALL,
	},
	{
		label: 'Bar chart',
		value: CHART_TYPES.BAR,
	},
];

export const CHART_TYPE_OPTIONS = [
	{
		label: 'Waterfall chart',
		value: CHART_TYPES.WATERFALL,
	},
	{
		label: 'Stacked bar chart',
		value: CHART_TYPES.STACKED,
	},
	{
		label: '100% stacked bar chart',
		value: CHART_TYPES.STACKED100,
	},
];

export const SPEND_TYPE = {
	INDIRECT: 1,
	DIRECT: 0,
};

export const CATEGORY_TYPE = {
	AP: 'apcategory',
	CLIENT: 'clientCategory',
	DEFAULT: 'category',
};

export const TAXONOMY_TYPES = {
	AP: 3,
	CLIENT: 2,
	PROJECT: 1,
};

export const EXPORT_OPTIONS = [
	{
		label: 'Export as image',
		value: 'png',
	},
];

export const NAV_CONFIG = [
	{
		path: '/spend',
		name: 'spend',
		label: 'Investigate',
	},
	{
		path: '/spend/explore',
		name: 'transactions',
		label: 'Transactions',
	},
	{
		path: '/spend/benchmarks',
		name: 'benchmarks',
		label: 'Benchmarks',
	},
	{
		path: '/spend/pivot-table',
		name: 'pivot-table',
		label: 'Pivot Table',
	},
];
export const SPEND_TYPE_LABELS = ['Direct', 'Indirect'];
export const UNKNOWN = 'UNKNOWN';
export const makeEmptyFromUnknown = val => (val === UNKNOWN ? '' : val);
export const makeUnknownFromEmpty = val => (val === '' ? UNKNOWN : val);

export const convertToArray = data =>
	data ? (Array.isArray(data) ? data : [data]) : [];

export const getCurrencySymbolFromOptions = (options, currency) => {
	if (!options) {
		return currency ? currency.toUpperCase() : '';
	}

	if (!currency) {
		return '';
	}

	const currencyObj =
		options.find(item => item.code === currency.toLowerCase()) || {};

	return currencyObj.symbol || currency.toUpperCase();
};

export const CATEGORY_BY_TAXONOMY = {
	[TAXONOMY_TYPES.PROJECT]: CATEGORY_TYPE.DEFAULT,
	[TAXONOMY_TYPES.CLIENT]: CATEGORY_TYPE.CLIENT,
	[TAXONOMY_TYPES.AP]: CATEGORY_TYPE.AP,
};

export const getCategoryParam = ({
	spendType,
	indirectTaxonomy,
	directTaxonomy,
}) => {
	if (spendType == null) {
		return CATEGORY_TYPE.DEFAULT;
	}
	const taxonomy =
		spendType === SPEND_TYPE.DIRECT ? directTaxonomy : indirectTaxonomy;

	return CATEGORY_BY_TAXONOMY[taxonomy] || CATEGORY_TYPE.DEFAULT;
};

export const InsightsType = [
	{
		id: 0,
		value: 'all insights',
		label: 'All Insights',
		isMaterial: true,
		iconName: 'filter_alt',
	},
	{
		id: 1,
		value: 'spend',
		label: 'Spend',
		isMaterial: true,
		iconName: 'bar_chart',
	},
	{
		id: 2,
		value: 'compliance',
		label: 'Compliance',
		isMaterial: true,
		iconName: 'search',
	},
	{
		id: 3,
		value: 'risk',
		label: 'Risk',
		isMaterial: true,
		iconName: 'error',
	},
	{
		id: 4,
		value: 'positive insights',
		label: 'Positive Insights',
		isMaterial: true,
		iconName: 'arrow_upward',
	},
	{
		id: 5,
		value: 'negative insights',
		label: 'Negative Insights',
		isMaterial: true,
		iconName: 'arrow_downward',
	},
];

export const getMonthPickerSrcRange = () => {
	const data = this.state.periodsData;
	return {
		from: {
			year: data[0].year,
			month: getMonthByDate(data[0].dateFrom) + 1,
		},
		to: {
			year: data[data.length - 1].year,
			month: getMonthByDate(data[data.length - 1].dateTo) + 1,
		},
	};
};

export const getMinMaxDatesFromData = data => {
	const filteredData = data.filter(el => el.isCustom === false);
	const dateRowLength = filteredData.length;
	const sortedDateRanges = filteredData.sort((a, b) => a.name - b.name);

	const minDate = sortedDateRanges[0].dateFrom.split('T')[0];
	const maxDate = sortedDateRanges[dateRowLength - 1].dateTo.split('T')[0];

	return { minDate, maxDate };
};

export const getDateRangeMonthYearSrc = (dateFrom, dateTo) => {
	const fromDate = new Date(dateFrom);
	const toDate = new Date(dateTo);

	const fromYear = fromDate.getFullYear();
	const fromMonth = fromDate.getMonth() + 1;

	const toYear = toDate.getFullYear();
	const toMonth = toDate.getMonth() + 1;

	const result = {
		from: {
			year: fromYear,
			month: fromMonth,
		},
		to: {
			year: toYear,
			month: toMonth,
		},
	};
	return result;
};

export const getDateRangeMonthYearState = (dateFrom, dateTo) => {
	const months = [
		'jan',
		'feb',
		'mar',
		'apr',
		'may',
		'jun',
		'jul',
		'aug',
		'sep',
		'oct',
		'nov',
		'dec',
	];

	const fromDate = new Date(dateFrom);
	const toDate = new Date(dateTo);

	const fromMonthName = months[fromDate.getMonth()];
	const toMonthName = months[toDate.getMonth()];

	const result = {
		from: {
			month: fromMonthName,
			monthNumber: fromDate.getMonth() + 1,
			year: fromDate.getFullYear(),
		},
		to: {
			month: toMonthName,
			monthNumber: toDate.getMonth() + 1,
			year: toDate.getFullYear(),
		},
	};

	return result;
};

export const getDateFormat = data => {
	const fromMonthNumber = data.from.monthNumber.toString().padStart(2, '0');
	const toMonthNumber = data.to.monthNumber.toString().padStart(2, '0');
	const monthsOf30Days = [4, 6, 9, 11];
	const isLeapYear = year => {
		return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
	};

	const lastdate =
		toMonthNumber === '02'
			? isLeapYear(data.to.year)
				? '29'
				: '28'
			: monthsOf30Days.includes(data.to.monthNumber)
				? '30'
				: '31';
	const fromDate = `${data.from.year}-${fromMonthNumber}-01`;
	const toDate = `${data.to.year}-${toMonthNumber}-${lastdate}`;

	return {
		dateFrom: fromDate,
		dateTo: toDate,
	};
};

export const checkAndAssignDefaultValues = (obj, defaultValue) => {
	for (const key in obj) {
		if (obj.hasOwnProperty(key) && obj[key] === null) {
			obj[key] = defaultValue;
		}
	}
	return obj;
};

export const allOption = {
	id: '/All/',
	originalId: '/All/',
	name: 'All',
	title: 'All',
	expanded: false,
	parentId: '/',
	value: null,
	children: [],
};
